/**
 * Client-side seller submit guardrails (M8).
 * In-memory only — no seller PII in storage.
 */
import { SELLER_SUBMISSION_STATUSES } from "./sellerIntakeConstants";
import { getSellerResultMessage } from "./sellerIntakeResult";

const inFlightSubmissionTokens = new Set();
const recentSubmissionTokens = new Map();

/** How long to remember a completed token in memory (ms). */
export const SELLER_CLIENT_SUBMISSION_TOKEN_TTL_MS = 60_000;

/**
 * @returns {string}
 */
export function createSellerSubmissionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `ss-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {string|null|undefined} submissionToken
 * @returns {boolean}
 */
export function tryAcquireSellerSubmissionToken(submissionToken) {
  const token = submissionToken ? String(submissionToken).trim() : "";
  if (!token) return true;

  if (inFlightSubmissionTokens.has(token)) {
    return false;
  }

  const recentAt = recentSubmissionTokens.get(token);
  if (recentAt && Date.now() - recentAt < SELLER_CLIENT_SUBMISSION_TOKEN_TTL_MS) {
    return false;
  }

  inFlightSubmissionTokens.add(token);
  return true;
}

/**
 * @param {string|null|undefined} submissionToken
 * @param {{ succeeded?: boolean }} [options]
 */
export function releaseSellerSubmissionToken(submissionToken, { succeeded = false } = {}) {
  const token = submissionToken ? String(submissionToken).trim() : "";
  if (!token) return;

  inFlightSubmissionTokens.delete(token);
  if (succeeded) {
    recentSubmissionTokens.set(token, Date.now());
  }
}

/**
 * @returns {import('./sellerIntakeTypes').SellerSubmissionResult & { formErrors?: Record<string, string> }}
 */
export function buildClientInFlightDuplicateResult() {
  const sellerMessage = getSellerResultMessage(SELLER_SUBMISSION_STATUSES.DUPLICATE);
  return {
    ok: false,
    status: SELLER_SUBMISSION_STATUSES.DUPLICATE,
    submissionId: null,
    sellerMessage,
    retryable: false,
    fieldErrors: [],
    errorCategory: SELLER_SUBMISSION_STATUSES.DUPLICATE,
    submittedAt: new Date().toISOString(),
    formErrors: { form: sellerMessage },
  };
}

/**
 * Resets in-memory guardrail state (tests only).
 */
export function resetSellerSubmissionGuardrailsForTests() {
  inFlightSubmissionTokens.clear();
  recentSubmissionTokens.clear();
}
